// frontend/src/App/hooks/useAuth.ts

/**
 * 🔐 AUTH HOOK - Gestión de autenticación con refresh automático
 *
 * Responsabilidad:
 * - Verificar si el usuario está autenticado
 * - Manejar logout manual y automático
 * - Integrar con authRefreshService para logout automático en refresh fallido
 *
 * Integración con Refresh Tokens:
 * - Configura callback de logout en authRefreshService
 * - Cuando el refresh falla → logout automático se ejecuta vía callback
 * - Previene múltiples logouts simultáneos con flag isLoggingOut
 *
 * @see Phase 1, Task 1.2 - ROADMAP_TO_10.md (Frontend - Día 2)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ROUTE_AUTH_ME, ROUTE_AUTH_LOGOUT } from '../../config/routes/routeConfig';
import { fetchWithAuth } from '../../shared/utils/helpers/fetchUtils';
import { authRefreshService } from '../../shared/services/authRefreshService';
import { logger } from '../../shared/utils/logger';

interface UseAuthReturn {
	isLoggedIn: boolean;
	setIsLoggedIn: (value: boolean) => void;
	authChecked: boolean;
	handleLogout: () => Promise<void>;
}

/**
 * Hook personalizado para manejar la autenticación
 * Verifica si el usuario está autenticado y maneja el logout
 */
export const useAuth = (): UseAuthReturn => {
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
	const [authChecked, setAuthChecked] = useState<boolean>(false);

	// Flag para prevenir múltiples logouts simultáneos
	const isLoggingOut = useRef<boolean>(false);

	/**
	 * Logout handler con protección contra múltiples ejecuciones
	 *
	 * Se usa como callback en authRefreshService para logout automático
	 * También se puede llamar manualmente desde la UI
	 */
	const handleLogout = useCallback(async (): Promise<void> => {
		// Prevenir múltiples logouts simultáneos
		if (isLoggingOut.current) {
			return;
		}

		isLoggingOut.current = true;

		try {
			// Llamar al endpoint de logout (revoca refresh token en backend)
			await fetchWithAuth(ROUTE_AUTH_LOGOUT, {
				method: 'POST',
			});

			// Actualizar estado de autenticación
			setIsLoggedIn(false);
		} catch (error) {
			logger.error('Logout failed', {
				error: error instanceof Error ? error.message : String(error),
			});

			// Aún en caso de error, consideramos que el usuario está deslogueado
			// (token inválido o expirado, mejor forzar re-login)
			setIsLoggedIn(false);
		} finally {
			isLoggingOut.current = false;
		}
	}, []);

	/**
	 * Configurar callback de logout en authRefreshService
	 * Se ejecutará automáticamente cuando el refresh falle
	 */
	useEffect(() => {
		authRefreshService.setLogoutCallback(handleLogout);
	}, [handleLogout]);

	/**
	 * Verificar autenticación al montar el componente
	 */
	useEffect(() => {
		const checkAuth = async (): Promise<void> => {
			try {
				const response = await fetchWithAuth(ROUTE_AUTH_ME);
				setIsLoggedIn(response.ok);
			} catch (error) {
				logger.error('Auth check failed', {
					error: error instanceof Error ? error.message : String(error),
				});
				setIsLoggedIn(false);
			} finally {
				setAuthChecked(true);
			}
		};

		checkAuth();
	}, []);

	return {
		isLoggedIn,
		setIsLoggedIn,
		authChecked,
		handleLogout,
	};
};
